import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import {ClosableDialogTitle} from '@components/dialog/closable-title/ClosableDialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import StepNav from './StepNav';
import confirm from 'utils/confirm';
import noop from 'lodash/noop';
import ToastActions from '@store/toast/ToastActions';
import {unsavedMessage} from 'utils/const';

export default class Wizard extends React.PureComponent {
  static propTypes = {
    // immutable list of components for each step
    steps: PropTypes.object,
    // function for rendering dynamic steps.  Receives the
    // current state of the model as a param
    getSteps: PropTypes.func,
    // react component to show on complete
    complete: PropTypes.func,
    // promise generating function for saving the model to the server
    submit: PropTypes.func,
    // called when user tries to close wizard
    onClose: PropTypes.func,
    // called when the user has finished everything successfully - model created
    onCompleted: PropTypes.func,
    // call back for when model is updated, allows for processing and
    // returning updated version
    onUpdateModel: PropTypes.func,
    // all other props get pass into children
  };

  constructor(props) {
    super(props);

    this.changeStep = this.changeStep.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.onRequestClose = this.onRequestClose.bind(this);
    this.submit = this.submit.bind(this);
    this.updateModel = this.updateModel.bind(this);

    const {model} = this.props;
    this.state = {
      step: props.startStep || 0,
      isComplete: false,
      model,
      initialModel: model,
      showErrors: false,
      completedSteps: new Set(),
      submitting: false,
    };
  }
  // TODO move model out of wizard state and push it upward to the
  // wrapper component.  Keeping it here makes it hard to update it from
  // above
  updateModel(model) {
    this.props.onUpdateModel && this.props.onUpdateModel(model);
    this.setState({model});
  }

  changeStep(newStep) {
    this.setState({step: newStep, showErrors: false});
  }

  nextStep() {
    const {step, model} = this.state;
    const validate = this.getSteps().get(step).validate;
    const errors = validate ? validate(model) : {};
    if (Object.keys(errors).length === 0) {
      this.setState({
        step: step + 1,
        showErrors: false,
        completedSteps: this.state.completedSteps.add(step),
      });
    } else {
      this.setState({showErrors: true});
    }
  }

  async submit() {
    const {submitting} = this.state;
    if (submitting) {
      return;
    }

    this.setState({submitting: true});
    const [step, error] =
      this.getSteps()
        .map(step => (step.validate ? step.validate(this.state.model) : {}))
        .findEntry(errors => Object.keys(errors).length > 0) || [];

    if (error) {
      this.setState({step, showErrors: true, submitting: false});
    } else {
      try {
        await this.props.submit(this.state.model);
        this.props.complete
          ? this.setState({
              isComplete: true,
              submitting: false,
            })
          : this.props.onCompleted();
      } catch (e) {
        console.error(e);
        this.setState({
          submitting: false,
        });
        ToastActions.addToast({
          type: 'error',
          // TODO should pull this out
          message: 'There was a problem saving to the server.',
        });
      }
    }
  }

  onRequestClose() {
    const {isComplete, model, initialModel} = this.state;
    if (isComplete) return this.props.onCompleted();

    if (model.equals(initialModel)) {
      this.props.onClose();
    } else {
      confirm(unsavedMessage).then(this.props.onClose, noop);
    }
  }

  getSteps() {
    const {getSteps, steps} = this.props;
    return getSteps ? getSteps(this.state.model) : steps;
  }

  render() {
    const {title, className, frozenSteps} = this.props;
    const steps = this.getSteps();
    const {step, model} = this.state;
    const CurrentStep = steps.get(step);
    const Complete = this.props.complete;
    return (
      <Dialog
        title={title}
        className={className}
        open={true}
        onRequestClose={this.onRequestClose}
        maxWidth={false}>
        <ClosableDialogTitle onClose={this.onRequestClose}>{title}</ClosableDialogTitle>
        <DialogContent>
          {this.state.isComplete ? (
            <Complete {...this.props} model={model} />
          ) : (
            <>
              <StepNav
                steps={steps.map(step => step.title)}
                current={step}
                changeStep={this.changeStep}
              />
              <h2>{CurrentStep.title}</h2>
              <CurrentStep
                {...this.props}
                step={step}
                model={model}
                onChange={this.updateModel}
                showErrors={this.state.showErrors}
                changeStep={this.changeStep}
                completedSteps={this.state.completedSteps}
                frozen={frozenSteps.includes(step)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {this.state.isComplete ? (
            <Button
              variant="contained"
              color="primary"
              className="ev-button"
              key="raisedButton"
              onClick={this.props.onCompleted}>
              Great, let's do this
            </Button>
          ) : (
            <>
              {step !== 0 && (
                <Button
                  variant="outlined"
                  className="ev-button"
                  key="raisedButton0"
                  onClick={() => this.changeStep(step - 1)}>
                  Previous
                </Button>
              )}
              {step === this.getSteps().size - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  className="ev-button"
                  key="raisedButton1"
                  onClick={() => this.submit()}>
                  Save
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  className="ev-button"
                  key="raisedButton2"
                  onClick={this.nextStep}>
                  Next
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}
